/**
 * SoundHost — the host half of project sound (`.kiro/specs/sound-and-music/`, design §2,
 * the exact shapes in `contract.md`).
 *
 * A project never touches audio bytes. Its frame posts `wbm:sound` naming a library id
 * (or a note), and this host plays it with everything the host already owns: the Sound
 * tab's channels, the world's own ambient (ducked under a project loop), Ember's voice
 * (project buses dip while she speaks), the native shell's audio session. Nothing about a
 * made project's sandbox changes, so an old project plays exactly as before (R2.4).
 *
 * One host per page, many frames: each framing surface attaches its iframe with
 * `useSoundHost`, and everything a frame started stops when that frame goes (R4.6).
 *
 * Every failure is silence plus an observability event, never an error a kid sees (R4.7).
 * The log the eval probe reads (`contract.md` §6) is mutated in place as an op settles —
 * a play is "queued" until its buffer decodes, then "played" — so a walk reads the final
 * word for each `seq`, once.
 */
import { observability } from "../api";
import { musicEnabled, sfxEnabled, useAudioSettings, WORLD_AUDIO_SFX_ENV_ENABLED, type AudioSettingsState } from "../world/audio/useAudioSettings";
import { getActiveWorldAudio } from "../world/audio/worldAudioRegistry";
import { isInstrument, playNote, resolvePitch, type Instrument, type NoteVoice, type ResolvedPitch } from "./noteSynth";
import { webSoundPlayback } from "../platform/soundSession";
import { evalMuteEnabled } from "../eval/probeFlag";
import {
  fetchSoundIndex,
  projectSoundEnabled,
  soundFileUrl,
  SOUND_ID_RE,
  type SoundIndex,
  type SoundRow,
} from "./soundIndex";

export type SoundSurface = "canvas" | "sandbox" | "visitor" | "shared" | "anonymous" | "oversight" | "world";
export type SoundOp = "play" | "ambient" | "music" | "note" | "stop" | "stop_all" | "preload" | "gesture" | "volume";
export type VolumeWord = "quiet" | "normal" | "loud";
/** Where a one-shot's buffer starts (U8, contract §10): at its hit, or at the file's start. */
export type AlignWord = "hit" | "start";
export type SoundResult =
  | "played" | "replaced" | "queued" | "stopped" | "missing" | "withdrawn"
  | "capped" | "muted" | "dropped" | "disabled" | "set";
export type MuteReason = "sound_tab" | "env";
/**
 * Why a one-shot was dropped (contract §6): the frame's 17th distinct sound (`library.md`
 * §4's cap), the tab hidden, no gesture yet with no resume in flight, a fetch or decode
 * failure, or a queued shot whose resume came too late.
 */
export type DropReason = "distinct_cap" | "hidden" | "no_gesture" | "load_error" | "stale";
type BusName = "sfx" | "ambient" | "music";

/** `wbm:sound`, as the frame sends it (contract §1). */
export interface Repeat {
  times: number;
  gap_ms: number;
  fade?: number;
}

export interface WbmSoundMessage {
  type: "wbm:sound";
  v: 1;
  seq: number;
  op: SoundOp;
  id?: string | null;
  /** `tune` (contract §3.1): the notes in order, `null` a rest; `pitch` is its first note, so a
   *  host without the clause plays that one. */
  note?: { instrument: string; pitch: number | string; tune?: (number | string | null)[] };
  /** A tune's binding: the same id again restarts a running copy instead of layering it. */
  tune_id?: number;
  opts?: {
    volume?: VolumeWord;
    repeat?: Repeat;
    /** Default "hit" for `sfx.event.*`, "start" otherwise (`hitOffsetMs`). */
    align?: AlignWord;
    /** A tune's pace: ms between note starts (`TUNE_GAP_*`). */
    gap_ms?: number;
    /** `stop` (contract §13): fade the id out over this long, then stop it; 0 or absent is a hard stop. */
    fade_ms?: number;
    /** `volume` (contract §13): the id's level in this frame, 0..1, reached over `ramp_ms`. */
    level?: number;
    ramp_ms?: number;
  };
}

const OPS: ReadonlySet<string> = new Set<SoundOp>(["play", "ambient", "music", "note", "stop", "stop_all", "preload", "gesture", "volume"]);
/** How long a one-shot waits for a resume that a gesture started (then it is dropped). */
const PENDING_SHOT_MS = 1500;
/** Every event that may carry a user activation; iOS WebKit honours touchend and click only. */
const GESTURE_EVENTS = ["pointerdown", "keydown", "touchend", "click"] as const;

export function isWbmSoundMessage(d: unknown): d is WbmSoundMessage {
  if (typeof d !== "object" || d === null) return false;
  const m = d as Record<string, unknown>;
  return m.type === "wbm:sound" && m.v === 1 && typeof m.seq === "number" && typeof m.op === "string" && OPS.has(m.op);
}

export interface FrameMeta {
  surface: SoundSurface;
  projectId: string | null;
  vertical: string | null;
  /** The signed-in user, for observability; null on an anonymous surface (then nothing is emitted). */
  userId: string | null;
  /**
   * With no userId (the share island, no AuthProvider): the session-gated sender
   * (`controls/telemetry.ts` `reportSessionEvent`), which sends for a cookie session and drops
   * for none (contract §12). Unset: nothing is emitted, as before.
   */
  report?: (eventType: string, projectId: string | null, data: Record<string, unknown>) => void;
}

export interface SoundLogEntry {
  seq: number;
  t: number;
  surface: SoundSurface;
  op: SoundOp;
  requested: string | null;
  id: string | null;
  note?: { instrument: string; pitch: number | string };
  /** A tune's note: its place in the tune and the tune's length (rests included). */
  tune?: { i: number; n: number };
  /** A note name outside C4–C6 that played an octave or more inside it: "E6→E5". */
  folded?: string;
  result: SoundResult;
  reason?: MuteReason | DropReason;
  /** play only (U8): the ms the host skipped so the hit lands, and when it estimates the
   *  first hit reached the output (the start plus the context's output latency). */
  offset_ms?: number;
  hit_at?: number;
  /** The output latency inside hit_at: the device's share, not the host's (a Bluetooth route
   *  adds ~150 ms that no scheduling can save). */
  latency_ms?: number;
  /** volume only: the level set. */
  level?: number;
}

export interface SoundProbe {
  enabled: boolean;
  unlocked: boolean;
  ctxState: "none" | "suspended" | "running" | "closed";
  indexed: number;
  index: "live" | "fixture";
  buses: {
    sfx: { gain: number; duck: number; id: null };
    ambient: { gain: number; duck: number; id: string | null };
    music: { gain: number; duck: number; id: string | null };
  };
  worldAmbientGain: number;
  voices: number;
  /** The share page's own mute (contract §15): the master is at 0, everything else runs. */
  pageMuted: boolean;
  log: SoundLogEntry[];
}

// ---- the numbers the host owns (design §2, §3) ----------------------------------------
export const MAX_VOICES = 8;
export const MAX_STARTS_PER_ID = 4;
export const START_WINDOW_MS = 250;
export const LOOP_FADE_MS = 400;
/** `stop` fades and `volume` ramps are clamped to this (contract §13). */
export const FADE_MAX_MS = 2000;
const LEVEL_RAMP_DEFAULT_MS = 50;
export const WORLD_DUCK = 0.15;
export const VOICE_DUCK = 0.3;
const VOICE_DUCK_MS = 150;
/** library.md §4: one fetch + decode each on first play, so a cap for load time (12 until
 *  2026-10-27; Warnock's 9 house sounds + 4 voice lines made it 16). The backend's
 *  MAX_DISTINCT_SOUNDS is the same number. */
const MAX_DISTINCT_PER_FRAME = 16;
/** A tune (contract §3.1): at most this many notes, this far apart by default, clamped. */
export const TUNE_MAX = 16;
export const TUNE_GAP_DEFAULT_MS = 250;
export const TUNE_GAP_MIN_MS = 80;
export const TUNE_GAP_MAX_MS = 1000;
/** Decoded PCM, not file bytes: a 28 s stereo bed alone is ~11 MB at 48 kHz, so at 12 MB a
 *  bed plus 8 effects evicted every effect after each play and the next hit was late by a
 *  decode (2026-10-27). 32 MB holds a 30 s bed and 16 short sounds. */
const CACHE_BYTES = 32 * 1024 * 1024;
const LOG_MAX = 200;
/**
 * An unknown id refetches the index once it is this old, at most once per this long per page
 * (contract §12): a tab opened before a library change otherwise keeps the old index for its
 * whole life and drops every new id (the Warnock voices on dev, 2026-10-27).
 */
export const INDEX_REFRESH_MS = 2 * 60 * 1000;
const PLAYED_SAMPLE = 20;
const CAPPED_SAMPLE = 20;
const REPEAT_MAX = 6;
const REPEAT_FADE_DEFAULT = 0.6;

/** The volume words, as gains relative to the bus: quiet −12 dB, normal 0, loud +4 dB. */
const VOLUME: Record<VolumeWord, number> = { quiet: 0.25, normal: 1, loud: 1.58 };
/** Where each bus sits under the voice (library.md §4: ambient −26, music −22, sfx −18 LUFS). */
const BUS_BASE: Record<BusName, number> = { sfx: 1, ambient: 0.5, music: 0.6 };
/** Surfaces where a project's music bed ignores the app's Background music switch (operator,
 *  2026-10-27): that switch is about the town's own music, and a shared project plays as it was
 *  made. The share page (the island and the SPA's), and a friend's project in their town. The
 *  build flag still applies; inside the app (editor, oversight, the town) nothing changes. */
const BED_IGNORES_MUSIC_PREF: ReadonlySet<SoundSurface> = new Set<SoundSurface>(["anonymous", "shared", "visitor"]);

/**
 * How far into the buffer a one-shot starts so its first hit lands on the event (U8, design
 * §16.1 part 2). "hit" skips to the row's `onset_ms`; "start" plays from 0 and the hit lands
 * `onset_ms` late. Unset, an event sound (`sfx.event.*`) aligns to its hit, a noun to its
 * start. A row without `onset_ms`, or with `hits_ms: []` (no hit), starts at 0 whatever the
 * word says: an index from before the ingest wrote onsets plays exactly as it did.
 */
export function hitOffsetMs(row: Pick<SoundRow, "id" | "onset_ms" | "hits_ms">, align: unknown): number {
  const word: AlignWord = align === "hit" || align === "start" ? align : row.id.startsWith("sfx.event.") ? "hit" : "start";
  if (word !== "hit") return 0;
  const onset = row.onset_ms;
  if (typeof onset !== "number" || !Number.isFinite(onset) || onset <= 0) return 0;
  if (Array.isArray(row.hits_ms) && row.hits_ms.length === 0) return 0;
  return Math.floor(onset);
}

/** A fade or ramp in ms from the frame, as clamped seconds (contract §13); anything else is 0. */
function msToS(ms: unknown): number {
  const n = Number(ms);
  return Number.isFinite(n) && n > 0 ? Math.min(FADE_MAX_MS, n) / 1000 : 0;
}

function volumeOf(word: unknown): number {
  return typeof word === "string" && word in VOLUME ? VOLUME[word as VolumeWord] : 1;
}

interface Voice {
  id: string;
  frameId: string;
  stop(): void;
  /** A buffer voice's own gain and its volume word's value (contract §13); a note has none. */
  gain?: GainNode;
  base?: number;
  /** Ramp to silence over `s` seconds, then stop. */
  fadeOut?(s: number): void;
}

interface Loop {
  id: string;
  frameId: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
  /** The volume word's value; the gain is this times the frame's level for the id. */
  base: number;
}

interface Bus {
  /** The channel: 1 when the Sound tab allows it, 0 when not. */
  channel: GainNode;
  /** The dip under Ember's voice. */
  duck: GainNode;
  loop: Loop | null;
}

interface Wanted {
  id: string;
  volume: number;
  entry: SoundLogEntry;
}

interface Frame {
  meta: FrameMeta;
  voices: Set<Voice>;
  /** Loops this frame asked for that could not start yet (no gesture, channel off, tab hidden). */
  wanted: { ambient: Wanted | null; music: Wanted | null };
  distinct: Set<string>;
  timers: Set<ReturnType<typeof setTimeout>>;
  /** A running tune's pending notes, by `tune_id`, so a retap can cancel them. */
  tunes: Map<number, Set<ReturnType<typeof setTimeout>>>;
  /** `wbm.soundVolume` levels by id (contract §13): applied to playing copies and to later starts. */
  levels: Map<string, number>;
}

interface CachedBuffer {
  promise: Promise<AudioBuffer | null>;
  buffer: AudioBuffer | null;
  bytes: number;
  lastUsed: number;
}

type EmitFn = (event: { event_type: string; user_id: string; project_id?: string; event_data: Record<string, unknown> }) => void;

/**
 * The platform adapter's audio-session pair (iPad shell train 13, `PlatformAdapter`): asked
 * for on the first sound that actually plays, never on mount; released when the project's
 * sound stops for good. The SPA hands the adapter in at boot (`audio/boot.ts`); a page that
 * hands nothing in (the prod share island) gets the browser's own session
 * (`platform/soundSession.ts`), so the silent switch does not mute it on iOS Safari.
 */
export interface PlaybackSession {
  requestSoundPlayback(): Promise<void>;
  releaseSoundPlayback(): Promise<void>;
}

export interface SoundHostOptions {
  enabled?: boolean;
  /** Makes the platform's session on first need (so nothing is made at boot either).
   *  Unset: the browser's session. */
  playback?: () => PlaybackSession | null;
  createContext?: () => AudioContext | null;
  fetchIndex?: (opts?: { fresh?: boolean }) => Promise<SoundIndex>;
  fetchImpl?: typeof fetch;
  emit?: EmitFn;
  now?: () => number;
  /** The document to watch for gestures and visibility; `document` when there is one. */
  doc?: Document | null;
}

function defaultCreateContext(): AudioContext | null {
  try {
    const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    return Ctor ? new Ctor() : null;
  } catch {
    return null;
  }
}

function defaultEmit(event: Parameters<EmitFn>[0]): void {
  void observability.emit(event);
}

export class SoundHost {
  readonly enabled: boolean;
  private readonly createContext: () => AudioContext | null;
  private readonly fetchIndex: (opts?: { fresh?: boolean }) => Promise<SoundIndex>;
  private readonly fetchImpl: typeof fetch;
  private readonly emitFn: EmitFn;
  private readonly now: () => number;
  private readonly doc: Document | null;

  private ctx: AudioContext | null = null;
  private buses: Record<BusName, Bus> | null = null;
  private master: GainNode | null = null;
  private pageMuted = false;
  private index: SoundIndex | null = null;
  private indexPromise: Promise<SoundIndex> | null = null;
  private indexRejectedReported = false;
  /** When the index in hand was last fetched or refetched; the refetch spacing reads it. */
  private indexAt = 0;
  private refreshing: Promise<SoundIndex> | null = null;
  private readonly buffers = new Map<string, CachedBuffer>();
  private readonly frames = new Map<string, Frame>();
  private readonly voices = new Set<Voice>();
  private readonly startsById = new Map<string, number[]>();
  private readonly log: SoundLogEntry[] = [];
  private playedCount = 0;
  private cappedCount = 0;
  private voiceDucked = false;
  private hidden = false;
  private listening = false;
  private gestureArmed = false;
  private onGesture: (() => void) | null = null;
  /** A `resume()` in flight after a gesture; one-shots that arrive meanwhile wait on it. */
  private resuming: Promise<void> | null = null;
  private readonly pendingShots: { frameId: string; msg: WbmSoundMessage; entry: SoundLogEntry; t: number }[] = [];
  private unsubscribeSettings: (() => void) | null = null;
  private disposed = false;
  private playbackFactory: (() => PlaybackSession | null) | null = null;
  private playback: PlaybackSession | null = null;
  /**
   * The host runs where no gesture is needed (the Capacitor shell, whose web view lifts the
   * requirement): the context resumes at prime and a loop asked for on entry starts at once
   * (contract §5, R4.3 relaxed on native). A browser keeps the gate as designed.
   */
  private gestureFree = false;
  /** The session is ours: asked for on a real play, not yet released. */
  private playbackHeld = false;

  constructor(opts: SoundHostOptions = {}) {
    this.enabled = opts.enabled ?? projectSoundEnabled();
    this.createContext = opts.createContext ?? defaultCreateContext;
    this.fetchImpl = opts.fetchImpl ?? ((...a) => fetch(...a));
    this.fetchIndex = opts.fetchIndex ?? ((o) => fetchSoundIndex(this.fetchImpl, undefined, o));
    this.emitFn = opts.emit ?? defaultEmit;
    this.now = opts.now ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));
    this.doc = opts.doc === undefined ? (typeof document !== "undefined" ? document : null) : opts.doc;
    this.playbackFactory = opts.playback ?? (() => webSoundPlayback);
  }

  /** The SPA's platform adapter, made on the first sound that plays (`audio/boot.ts`). */
  setPlaybackSession(factory: (() => PlaybackSession | null) | null): void {
    this.playbackFactory = factory;
    this.playback = null;
  }

  // ---- boot -----------------------------------------------------------------------------

  /**
   * Called at app boot (`audio/boot.ts`) and at every attach: make the context now, so the
   * first tap on OUR chrome (Make this, Start, Play, the gated open) is the gesture that
   * unlocks it, before any frame exists. Every flow that frames a project begins with one
   * of those taps, so the first tap inside the project then plays (evals, WebKit,
   * 2026-10-22). The page-level listener stays as the fallback for everything else.
   */
  prime(): void {
    if (!this.enabled || this.disposed) return;
    this.listen();
    this.ensureContext();
    if (this.unlocked()) return;
    if (this.gestureFree) this.unlock();
    else this.armGesture();
  }

  /** Native: no gesture is needed, so the context resumes now and on every prime. */
  setGestureFree(on: boolean): void {
    this.gestureFree = on;
    if (on && this.enabled && !this.disposed && this.ctx && !this.unlocked()) this.unlock();
  }

  // ---- frames ---------------------------------------------------------------------------

  attachFrame(frameId: string, meta: FrameMeta): void {
    if (this.disposed) return;
    this.frames.set(frameId, {
      meta,
      voices: new Set(),
      wanted: { ambient: null, music: null },
      distinct: new Set(),
      timers: new Set(),
      tunes: new Map(),
      levels: new Map(),
    });
    if (!this.enabled) return;
    this.prime();
    // The index is the one fetch every play waits on, so it starts at attach, not at the
    // first tap: a kid's first ribbit should not pay for the index.
    void this.ensureIndex();
  }

  /** The frame went away: everything it started stops, loops over 400 ms (R4.6). */
  detachFrame(frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    this.stopFrame(frame, frameId);
    this.frames.delete(frameId);
    this.resumeWanted();
    if (this.frames.size === 0) this.releasePlayback();
  }

  // ---- the message ----------------------------------------------------------------------

  /** Handle one `wbm:sound` from `frameId`. Returns the log entry, which may settle later. */
  handle(frameId: string, msg: WbmSoundMessage): SoundLogEntry | null {
    const frame = this.frames.get(frameId);
    if (!frame || this.disposed) return null;
    if (msg.op === "gesture") {
      // The frame's first touch (contract §5): a tap inside the project is a user gesture
      // in the frame's document, which the host's own listener never sees. Not logged: it
      // is not a sound. WebKit may still refuse a resume outside its own gesture task, which
      // is why `prime()` unlocks on the host's chrome first. Every relayed gesture asks
      // again: a tap posts on pointerdown, touchend and click, and iOS honours only the later
      // two, so the first ask may be the refused one (2026-10-27).
      if (this.enabled) this.unlock();
      return null;
    }
    const requested = this.requestedOf(msg);
    const entry: SoundLogEntry = {
      seq: msg.seq,
      t: this.now(),
      surface: frame.meta.surface,
      op: msg.op,
      requested,
      id: null,
      result: "queued",
    };
    if (msg.op === "note" && msg.note) entry.note = { instrument: msg.note.instrument, pitch: msg.note.pitch };
    // A distance loop sets an id's level many times a second: its latest entry replaces the one
    // before, so the log keeps the plays the probe reads.
    const last = this.log[this.log.length - 1];
    if (msg.op === "volume" && last?.op === "volume" && last.requested === requested) this.log.pop();
    this.push(entry);

    if (!this.enabled) {
      entry.result = "disabled";
      entry.reason = "env";
      return entry;
    }

    switch (msg.op) {
      case "stop_all":
        this.stopFrame(frame, frameId);
        entry.result = "stopped";
        return entry;
      case "stop":
        this.stopId(frame, frameId, msg.id ?? null, msToS(msg.opts?.fade_ms));
        entry.result = "stopped";
        return entry;
      case "volume":
        this.setLevel(frame, frameId, msg, entry);
        return entry;
      case "ambient":
      case "music":
        this.handleLoop(frame, frameId, msg.op, msg, entry);
        return entry;
      case "note":
        this.handleNote(frame, frameId, msg, entry);
        return entry;
      case "preload":
        void this.handlePreload(frame, msg, entry);
        return entry;
      case "play":
        this.handlePlay(frame, frameId, msg, entry);
        return entry;
    }
  }

  private requestedOf(msg: WbmSoundMessage): string | null {
    if (msg.op === "note") {
      const n = msg.note;
      return n ? `note.${String(n.instrument)}.${String(n.pitch)}` : "note.?";
    }
    return typeof msg.id === "string" ? msg.id : null;
  }

  // ---- play -----------------------------------------------------------------------------

  private handlePlay(frame: Frame, frameId: string, msg: WbmSoundMessage, entry: SoundLogEntry): void {
    const id = this.validId(msg.id);
    if (!id) { this.missing(frame, entry, "bad_id"); return; }
    if (!this.channelOn("sfx")) { entry.result = "muted"; entry.reason = "sound_tab"; return; }
    if (!this.unlocked()) { this.deferShot(frameId, msg, entry); return; }
    if (this.hidden) { entry.result = "dropped"; entry.reason = "hidden"; return; }
    if (this.capped(id)) { entry.result = "capped"; this.emitCapped(frame, id); return; }
    void this.withRow(frame, id, entry).then((resolved) => {
      if (!resolved || this.disposed || !this.frames.has(frameId)) return;
      const { row, result } = resolved;
      if (!this.admitDistinct(frame, row.id)) { entry.result = "dropped"; entry.reason = "distinct_cap"; return; }
      void this.buffer(row).then((buf) => {
        if (!buf || this.disposed || !this.frames.has(frameId)) { if (!buf) { entry.result = "dropped"; entry.reason = "load_error"; } return; }
        if (this.capped(row.id)) { entry.result = "capped"; this.emitCapped(frame, row.id); return; }
        const volume = volumeOf(msg.opts?.volume);
        const offsetMs = hitOffsetMs(row, msg.opts?.align);
        const startedAt = this.startOneShot(frame, frameId, row.id, buf, volume, offsetMs / 1000);
        this.scheduleRepeats(frame, frameId, row.id, buf, volume, msg.opts?.repeat, offsetMs / 1000);
        entry.id = row.id;
        entry.result = result;
        entry.offset_ms = offsetMs;
        if (startedAt !== null) {
          // Where the first hit lands: the start, the output's own latency, and whatever of the
          // lead was kept (all of it under "start", none under "hit").
          const onset = typeof row.onset_ms === "number" && row.onset_ms > 0 ? row.onset_ms : 0;
          const latency = this.outputLatencyMs();
          entry.hit_at = startedAt + latency + Math.max(0, onset - offsetMs);
          entry.latency_ms = Math.round(latency);
        }
        this.emitPlayed(frame, row, "play");
      });
    });
  }

  /** The context's output latency in ms, as far as it says (0 where it does not). */
  private outputLatencyMs(): number {
    const c = this.ctx as unknown as { outputLatency?: number; baseLatency?: number } | null;
    const s = c?.outputLatency ?? c?.baseLatency ?? 0;
    return typeof s === "number" && Number.isFinite(s) && s > 0 ? s * 1000 : 0;
  }

  /** Starts a voice; `offsetS` into the buffer (U8). Returns when it started, or null. */
  private startOneShot(frame: Frame, frameId: string, id: string, buf: AudioBuffer, volume: number, offsetS = 0): number | null {
    const ctx = this.ctx!;
    const bus = this.buses!.sfx;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume * (frame.levels.get(id) ?? 1), ctx.currentTime);
    gain.connect(bus.channel);
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.connect(gain);
    const voice: Voice = {
      id,
      frameId,
      gain,
      base: volume,
      stop: () => {
        try { source.stop(); } catch { /* already stopped */ }
        try { gain.disconnect(); } catch { /* ok */ }
      },
      fadeOut: (s: number) => {
        const t = ctx.currentTime;
        try {
          gain.gain.cancelScheduledValues(t);
          gain.gain.setValueAtTime(gain.gain.value, t);
          gain.gain.linearRampToValueAtTime(0, t + s);
          source.stop(t + s);
        } catch { voice.stop(); }
      },
    };
    source.onended = () => {
      this.voices.delete(voice);
      frame.voices.delete(voice);
      try { gain.disconnect(); } catch { /* ok */ }
    };
    this.voices.add(voice);
    frame.voices.add(voice);
    this.noteStart(id);
    // An offset past the buffer (a bad onset) would play nothing: start from 0 instead.
    const duration = typeof buf.duration === "number" && buf.duration > 0 ? buf.duration : Infinity;
    const offset = offsetS > 0 && offsetS < duration ? offsetS : 0;
    try { if (offset > 0) source.start(0, offset); else source.start(); } catch { this.voices.delete(voice); frame.voices.delete(voice); return null; }
    this.holdPlayback();
    return this.now();
  }

  /** The echo (design §3): `times` more plays, `gap_ms` apart, each `fade` as loud as the last. */
  private scheduleRepeats(
    frame: Frame, frameId: string, id: string, buf: AudioBuffer, volume: number, repeat: Repeat | undefined, offsetS = 0,
  ): void {
    if (!repeat || typeof repeat !== "object") return;
    const times = Math.min(REPEAT_MAX, Math.max(0, Math.floor(Number(repeat.times) || 0)));
    const gap = Math.max(50, Number(repeat.gap_ms) || 0);
    const fade = typeof repeat.fade === "number" && repeat.fade > 0 && repeat.fade < 1 ? repeat.fade : REPEAT_FADE_DEFAULT;
    for (let n = 1; n <= times; n++) {
      const timer = setTimeout(() => {
        frame.timers.delete(timer);
        if (this.disposed || !this.frames.has(frameId) || this.hidden || !this.channelOn("sfx")) return;
        if (this.voices.size >= MAX_VOICES) return;
        this.startOneShot(frame, frameId, id, buf, volume * Math.pow(fade, n), offsetS);
      }, gap * n);
      frame.timers.add(timer);
    }
  }

  // ---- loops ----------------------------------------------------------------------------

  private handleLoop(frame: Frame, frameId: string, bus: "ambient" | "music", msg: WbmSoundMessage, entry: SoundLogEntry): void {
    if (msg.id === null || msg.id === undefined) {
      frame.wanted[bus] = null;
      const current = this.buses?.[bus].loop;
      if (current && current.frameId === frameId) { this.stopLoop(bus); this.resumeWanted(); }
      entry.result = "stopped";
      return;
    }
    const id = this.validId(msg.id);
    if (!id) { this.missing(frame, entry, "bad_id"); return; }
    const volume = volumeOf(msg.opts?.volume);
    void this.withRow(frame, id, entry).then((resolved) => {
      if (!resolved || this.disposed || !this.frames.has(frameId)) return;
      const { row, result } = resolved;
      if (row.kind === "sfx") { this.missing(frame, entry, "not_a_loop"); return; }
      if (!this.admitDistinct(frame, row.id)) { entry.result = "dropped"; entry.reason = "distinct_cap"; return; }
      entry.id = row.id;
      // Remember what this frame wants on this bus; it starts when it can (gesture, the
      // Sound tab, the tab coming back) and the entry settles then (contract §5).
      frame.wanted[bus] = { id: row.id, volume, entry };
      if (!this.channelOn(bus, frame)) { entry.result = "muted"; entry.reason = "sound_tab"; return; }
      if (!this.unlocked() || this.hidden) { entry.result = "queued"; this.armGesture(); return; }
      void this.startWanted(frame, frameId, bus, result);
    });
  }

  private async startWanted(frame: Frame, frameId: string, bus: "ambient" | "music", result: SoundResult = "played"): Promise<void> {
    const wanted = frame.wanted[bus];
    if (!wanted) return;
    const row = this.index?.rows.get(wanted.id);
    if (!row) return;
    const buf = await this.buffer(row);
    if (!buf || this.disposed || !this.frames.has(frameId)) { if (!buf) { wanted.entry.result = "dropped"; wanted.entry.reason = "load_error"; } return; }
    if (frame.wanted[bus] !== wanted) return; // superseded while decoding
    if (!this.unlocked() || this.hidden || !this.channelOn(bus, frame)) return; // still waiting; settles later
    this.startLoop(frameId, bus, row, buf, wanted.volume);
    wanted.entry.result = result;
    this.emitPlayed(frame, row, bus);
  }

  private startLoop(frameId: string, bus: "ambient" | "music", row: SoundRow, buf: AudioBuffer, volume: number): void {
    const ctx = this.ctx!;
    const b = this.buses![bus];
    if (b.loop && b.loop.id === row.id && b.loop.frameId === frameId) return; // already playing
    // Another frame's loop gives way (a project's own ambient over the place's, R5.2 and
    // design §6) and is remembered as that frame's wish, so it comes back when this one goes.
    const old = b.loop;
    if (old && old.frameId !== frameId) {
      const other = this.frames.get(old.frameId);
      if (other && !other.wanted[bus]) {
        other.wanted[bus] = { id: old.id, volume: old.base, entry: this.last(old.frameId, bus) };
      }
    }
    this.stopLoop(bus);
    const level = this.frames.get(frameId)?.levels.get(row.id) ?? 1;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume * level, ctx.currentTime + LOOP_FADE_MS / 1000);
    gain.connect(b.channel);
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = true;
    if (row.loop && row.loop.end_s > row.loop.start_s) {
      source.loopStart = row.loop.start_s;
      source.loopEnd = row.loop.end_s;
    }
    source.connect(gain);
    try { source.start(); } catch { return; }
    b.loop = { id: row.id, frameId, source, gain, base: volume };
    if (bus === "music") this.rampChannel("music", this.musicChannelOn(useAudioSettings.getState()));
    this.duckWorld();
    this.holdPlayback();
  }

  private stopLoop(bus: "ambient" | "music", fadeS = LOOP_FADE_MS / 1000): void {
    const b = this.buses?.[bus];
    if (!b?.loop) return;
    const { source, gain } = b.loop;
    b.loop = null;
    const ctx = this.ctx!;
    try {
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + fadeS);
      source.stop(ctx.currentTime + fadeS);
    } catch { /* ok */ }
    this.duckWorld();
  }

  /**
   * The town's own loop (WorldAudioController) dips to 0.15 while a project loop plays and
   * comes back after (R4.4); under a PLACE's ambient (the world surface, R5.2) it goes to 0,
   * because the place sound plays in place of the town's, not over it (design §6).
   */
  private duckWorld(): void {
    const world = getActiveWorldAudio();
    if (!world) return;
    const loops = [this.buses?.ambient.loop, this.buses?.music.loop].filter((l): l is Loop => !!l);
    if (loops.length === 0) { world.duck(1); return; }
    const place = loops.some((l) => this.frames.get(l.frameId)?.meta.surface === "world");
    world.duck(place ? 0 : WORLD_DUCK);
  }

  /** A bus fell silent: whichever frame still wants a loop there gets it back. */
  private resumeWanted(): void {
    for (const [frameId, frame] of this.frames) {
      void this.startWanted(frame, frameId, "ambient");
      void this.startWanted(frame, frameId, "music");
    }
  }

  // ---- notes ----------------------------------------------------------------------------

  private handleNote(frame: Frame, frameId: string, msg: WbmSoundMessage, entry: SoundLogEntry): void {
    if (Array.isArray(msg.note?.tune)) { this.handleTune(frame, frameId, msg, entry, msg.note.tune); return; }
    const instrument = msg.note?.instrument;
    const at = resolvePitch(msg.note?.pitch);
    if (!isInstrument(instrument) || at === null) { this.missing(frame, entry, "bad_note"); return; }
    if (at.folded) entry.folded = at.folded;
    if (!this.channelOn("sfx")) { entry.result = "muted"; entry.reason = "sound_tab"; return; }
    if (!this.unlocked()) { this.deferShot(frameId, msg, entry); return; }
    if (this.hidden) { entry.result = "dropped"; entry.reason = "hidden"; return; }
    // `requested` keeps what the frame asked for; the id and the start cap are the note that sounds.
    const key = `note.${instrument}.${String(at.pitch)}`;
    if (this.capped(key)) { entry.result = "capped"; this.emitCapped(frame, key); return; }
    this.startNote(frame, frameId, key, instrument, at.hz, volumeOf(msg.opts?.volume));
    entry.id = key;
    entry.result = "played";
    this.playedCount++;
    if (this.playedCount % PLAYED_SAMPLE === 0) {
      this.emit(frame, "sound.played", { id: key, kind: "note", op: "note" });
    }
  }

  /**
   * A tune (contract §3.1): its notes `gap_ms` apart, scheduled here like `repeat`, so each
   * note meets the Sound tab, the voice cap and its pitch's start cap as a single note does.
   * One log entry per sounding note; the message's entry is the first one's. A rest sounds
   * nothing and logs nothing.
   */
  private handleTune(
    frame: Frame, frameId: string, msg: WbmSoundMessage, entry: SoundLogEntry, raw: readonly unknown[],
  ): void {
    const instrument = msg.note?.instrument;
    const tune = raw.slice(0, TUNE_MAX).map((p) => (typeof p === "number" || typeof p === "string" ? p : null));
    const ats = tune.map((p) => (p === null ? null : resolvePitch(p)));
    const bad = ats.some((at, i) => tune[i] !== null && at === null) || ats.every((at) => at === null);
    if (!isInstrument(instrument) || bad) { this.missing(frame, entry, "bad_note"); return; }
    if (!this.channelOn("sfx")) { entry.result = "muted"; entry.reason = "sound_tab"; return; }
    if (!this.unlocked()) { this.deferShot(frameId, msg, entry); return; }
    if (this.hidden) { entry.result = "dropped"; entry.reason = "hidden"; return; }
    let pending: Set<ReturnType<typeof setTimeout>> | null = null;
    if (typeof msg.tune_id === "number") {
      // The same binding again (a retap, a rule firing before its tune ended): start over.
      for (const t of frame.tunes.get(msg.tune_id) ?? []) { clearTimeout(t); frame.timers.delete(t); }
      pending = new Set();
      frame.tunes.set(msg.tune_id, pending);
    }
    const gap = Math.min(TUNE_GAP_MAX_MS, Math.max(TUNE_GAP_MIN_MS, Number(msg.opts?.gap_ms) || TUNE_GAP_DEFAULT_MS));
    const volume = volumeOf(msg.opts?.volume);
    let first: SoundLogEntry | null = entry;
    tune.forEach((pitch, i) => {
      const at = ats[i];
      if (pitch === null || !at) return;
      const own = first;
      first = null;
      const sound = () => this.tuneNote(frame, frameId, msg, own, instrument, pitch, at, i, tune.length, volume);
      if (i === 0) { sound(); return; }
      const timer = setTimeout(() => {
        frame.timers.delete(timer);
        pending?.delete(timer);
        sound();
      }, gap * i);
      frame.timers.add(timer);
      pending?.add(timer);
    });
  }

  private tuneNote(
    frame: Frame, frameId: string, msg: WbmSoundMessage, own: SoundLogEntry | null,
    instrument: Instrument, pitch: number | string, at: ResolvedPitch, i: number, n: number, volume: number,
  ): void {
    if (this.disposed || !this.frames.has(frameId)) return;
    const requested = `note.${instrument}.${String(pitch)}`;
    const key = `note.${instrument}.${String(at.pitch)}`;
    const entry: SoundLogEntry = own ?? {
      seq: msg.seq, t: this.now(), surface: frame.meta.surface, op: "note", requested, id: null, result: "queued",
    };
    if (!own) this.push(entry);
    entry.requested = requested;
    entry.note = { instrument, pitch };
    entry.tune = { i, n };
    if (at.folded) entry.folded = at.folded;
    if (this.hidden) { entry.result = "dropped"; entry.reason = "hidden"; return; }
    if (!this.channelOn("sfx")) { entry.result = "muted"; entry.reason = "sound_tab"; return; }
    if (this.capped(key)) { entry.result = "capped"; this.emitCapped(frame, key); return; }
    this.startNote(frame, frameId, key, instrument, at.hz, volume);
    entry.id = key;
    entry.result = "played";
    this.playedCount++;
    if (this.playedCount % PLAYED_SAMPLE === 0) {
      this.emit(frame, "sound.played", { id: key, kind: "note", op: "note" });
    }
  }

  private startNote(frame: Frame, frameId: string, key: string, instrument: Instrument, hz: number, volume: number): void {
    const ctx = this.ctx!;
    let note: NoteVoice;
    try {
      note = playNote(ctx, this.buses!.sfx.channel, instrument, hz, volume);
    } catch {
      return;
    }
    const voice: Voice = { id: key, frameId, stop: () => note.stop() };
    this.voices.add(voice);
    frame.voices.add(voice);
    this.noteStart(key);
    this.holdPlayback();
    const timer = setTimeout(() => {
      frame.timers.delete(timer);
      this.voices.delete(voice);
      frame.voices.delete(voice);
    }, Math.max(0, (note.endsAt - ctx.currentTime) * 1000) + 60);
    frame.timers.add(timer);
  }

  // ---- preload --------------------------------------------------------------------------

  private async handlePreload(frame: Frame, msg: WbmSoundMessage, entry: SoundLogEntry): Promise<void> {
    const id = this.validId(msg.id);
    if (!id) { this.missing(frame, entry, "bad_id"); return; }
    const resolved = await this.withRow(frame, id, entry);
    if (!resolved) return;
    if (!this.admitDistinct(frame, resolved.row.id)) { entry.result = "dropped"; entry.reason = "distinct_cap"; return; }
    entry.id = resolved.row.id;
    this.ensureContext();
    const buf = this.ctx ? await this.buffer(resolved.row) : null;
    entry.result = buf ? "queued" : "dropped";
  }

  // ---- stopping -------------------------------------------------------------------------

  private stopFrame(frame: Frame, frameId: string): void {
    for (const v of Array.from(frame.voices)) {
      v.stop();
      this.voices.delete(v);
    }
    frame.voices.clear();
    for (const t of frame.timers) clearTimeout(t);
    frame.timers.clear();
    frame.tunes.clear();
    frame.wanted.ambient = null;
    frame.wanted.music = null;
    for (const bus of ["ambient", "music"] as const) {
      const loop = this.buses?.[bus].loop;
      if (loop && loop.frameId === frameId) this.stopLoop(bus);
    }
  }

  /** `fadeS` 0 is today's hard stop for a one-shot; a loop always fades, LOOP_FADE_MS unless given. */
  private stopId(frame: Frame, frameId: string, id: string | null, fadeS = 0): void {
    if (!id) return;
    for (const v of Array.from(frame.voices)) {
      if (v.id !== id) continue;
      if (fadeS > 0 && v.fadeOut) v.fadeOut(fadeS); else v.stop();
      frame.voices.delete(v);
      this.voices.delete(v);
    }
    for (const bus of ["ambient", "music"] as const) {
      if (frame.wanted[bus]?.id === id) frame.wanted[bus] = null;
      const loop = this.buses?.[bus].loop;
      if (loop && loop.frameId === frameId && loop.id === id) this.stopLoop(bus, fadeS > 0 ? fadeS : undefined);
    }
  }

  /**
   * `wbm.soundVolume` (contract §13): the id's level in this frame, ramped on every playing copy
   * and its loop, and kept for later starts. Levels of ids never played still count nothing.
   */
  private setLevel(frame: Frame, frameId: string, msg: WbmSoundMessage, entry: SoundLogEntry): void {
    const id = typeof msg.id === "string" ? msg.id : null;
    const raw = Number(msg.opts?.level);
    if (!id || !Number.isFinite(raw)) { this.missing(frame, entry, "bad_id"); return; }
    const level = Math.min(1, Math.max(0, raw));
    frame.levels.set(id, level);
    entry.result = "set";
    entry.level = level;
    const ctx = this.ctx;
    if (!ctx) return;
    const rampS = msToS(msg.opts?.ramp_ms ?? LEVEL_RAMP_DEFAULT_MS);
    const to = (g: GainNode, v: number): void => {
      const t = ctx.currentTime;
      try {
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        if (rampS > 0) g.gain.linearRampToValueAtTime(v, t + rampS); else g.gain.setValueAtTime(v, t);
      } catch { /* ok */ }
    };
    for (const v of frame.voices) if (v.id === id && v.gain) to(v.gain, (v.base ?? 1) * level);
    for (const bus of ["ambient", "music"] as const) {
      const loop = this.buses?.[bus].loop;
      if (loop && loop.frameId === frameId && loop.id === id) to(loop.gain, loop.base * level);
    }
  }

  /** Every bus of every frame stops within 400 ms; loops are remembered for the next gesture. */
  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    if (!hidden) return;
    for (const [frameId, frame] of this.frames) {
      for (const v of Array.from(frame.voices)) { v.stop(); this.voices.delete(v); }
      frame.voices.clear();
      for (const t of frame.timers) clearTimeout(t);
      frame.timers.clear();
      frame.tunes.clear();
      for (const bus of ["ambient", "music"] as const) {
        const loop = this.buses?.[bus].loop;
        if (loop && loop.frameId === frameId) {
          const wanted = frame.wanted[bus];
          if (!wanted || wanted.id !== loop.id) {
            frame.wanted[bus] = { id: loop.id, volume: loop.gain.gain.value || 1, entry: this.last(frameId, bus) };
          }
          this.stopLoop(bus);
        }
      }
    }
    this.disarmGesture();
    this.armGesture();
  }

  private last(frameId: string, bus: "ambient" | "music"): SoundLogEntry {
    for (let i = this.log.length - 1; i >= 0; i--) {
      const e = this.log[i]!;
      if (e.op === bus && e.surface === this.frames.get(frameId)?.meta.surface) return e;
    }
    return { seq: 0, t: this.now(), surface: this.frames.get(frameId)!.meta.surface, op: bus, requested: null, id: null, result: "queued" };
  }

  // ---- Ember's voice --------------------------------------------------------------------

  /** Project buses dip to 0.3 while Ember speaks, and come back when she is done. */
  setVoiceDuck(on: boolean): void {
    this.voiceDucked = on;
    if (!this.ctx || !this.buses) return;
    const t = this.ctx.currentTime;
    for (const bus of Object.values(this.buses)) {
      try {
        bus.duck.gain.cancelScheduledValues(t);
        bus.duck.gain.setValueAtTime(bus.duck.gain.value, t);
        bus.duck.gain.linearRampToValueAtTime(on ? VOICE_DUCK : 1, t + VOICE_DUCK_MS / 1000);
      } catch { /* ok */ }
    }
  }

  // ---- the index and the buffers --------------------------------------------------------

  private ensureIndex(): Promise<SoundIndex> {
    if (this.index) return Promise.resolve(this.index);
    if (!this.indexPromise) {
      this.indexPromise = this.fetchIndex().then((idx) => {
        this.index = idx;
        this.indexAt = this.now();
        return idx;
      }).catch(() => {
        const empty: SoundIndex = { rows: new Map(), rejected: [], source: "live", base: "" };
        this.index = empty;
        this.indexAt = this.now();
        return empty;
      });
    }
    return this.indexPromise;
  }

  /**
   * One refetch of the index, shared by every unknown id that asks while it is in flight, or
   * null when the index in hand is younger than INDEX_REFRESH_MS (so a storm of unknown ids
   * costs one fetch). An empty answer (a failed fetch) never replaces the index in hand.
   */
  private refreshIndex(): Promise<SoundIndex> | null {
    if (this.refreshing) return this.refreshing;
    if (!this.index || this.now() - this.indexAt < INDEX_REFRESH_MS) return null;
    this.indexAt = this.now();
    const kept = this.index;
    this.refreshing = this.fetchIndex({ fresh: true })
      .then((idx) => {
        if (idx.rows.size === 0 || this.disposed) return this.index ?? kept;
        this.index = idx;
        this.indexRejectedReported = false;
        return idx;
      })
      .catch(() => this.index ?? kept)
      .finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  /**
   * The row for `id`, following `replaced_by` once. An id the index does not know refetches
   * a stale index once (refreshIndex) before it is dropped as unknown. Settles the entry itself on the ways it
   * can fail: unknown (`missing`), withdrawn with nothing in its place (`withdrawn`).
   */
  private async withRow(frame: Frame, id: string, entry: SoundLogEntry): Promise<{ row: SoundRow; result: SoundResult } | null> {
    let index = await this.ensureIndex();
    let row = index.rows.get(id);
    if (!row) {
      const fresh = this.refreshIndex();
      if (fresh) {
        index = await fresh;
        row = index.rows.get(id);
        if (row) this.emit(frame, "sound.index_refreshed", { id, rows: index.rows.size });
      }
    }
    this.reportRejected(frame, index);
    if (!row) { this.missing(frame, entry, "unknown"); return null; }
    if (row.withdrawn || row.replaced_by) {
      const to = typeof row.replaced_by === "string" ? index.rows.get(row.replaced_by) : undefined;
      // Followed exactly once: a target that is itself withdrawn or replaced is silence,
      // the same rule the backend loader applies.
      if (!to || to.withdrawn || to.replaced_by) {
        entry.result = "withdrawn";
        this.emit(frame, "sound.missing", { id, reason: "withdrawn" });
        return null;
      }
      return { row: to, result: "replaced" };
    }
    return { row, result: "played" };
  }

  private reportRejected(frame: Frame, index: SoundIndex): void {
    if (this.indexRejectedReported || index.rejected.length === 0) return;
    this.indexRejectedReported = true;
    this.emit(frame, "sound.index_rejected", { ids: index.rejected.slice(0, 50), count: index.rejected.length });
  }

  private buffer(row: SoundRow): Promise<AudioBuffer | null> {
    this.ensureContext();
    const ctx = this.ctx;
    if (!ctx || !this.index) return Promise.resolve(null);
    const cached = this.buffers.get(row.id);
    if (cached) { cached.lastUsed = this.now(); return cached.promise; }
    const url = soundFileUrl(this.index.base, row);
    const entry: CachedBuffer = { promise: Promise.resolve(null), buffer: null, bytes: 0, lastUsed: this.now() };
    entry.promise = (async () => {
      try {
        const res = await this.fetchImpl(url, { credentials: "omit" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bytes = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(bytes.slice(0));
        entry.buffer = buf;
        entry.bytes = buf.length * buf.numberOfChannels * 4;
        this.evict();
        return buf;
      } catch (e) {
        this.buffers.delete(row.id);
        this.emitAny("sound.load_error", { id: row.id, error: e instanceof Error ? e.message : String(e) });
        return null;
      }
    })();
    this.buffers.set(row.id, entry);
    return entry.promise;
  }

  /** Keep the cache near CACHE_BYTES: the least recently used decoded buffer not in a loop goes first. */
  private evict(): void {
    let total = 0;
    for (const b of this.buffers.values()) total += b.bytes;
    if (total <= CACHE_BYTES) return;
    const inLoop = new Set<string>();
    for (const bus of ["ambient", "music"] as const) {
      const id = this.buses?.[bus].loop?.id;
      if (id) inLoop.add(id);
    }
    const order = Array.from(this.buffers.entries())
      .filter(([id, b]) => b.buffer && !inLoop.has(id))
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [id, b] of order) {
      if (total <= CACHE_BYTES) break;
      this.buffers.delete(id);
      total -= b.bytes;
    }
  }

  private admitDistinct(frame: Frame, id: string): boolean {
    if (frame.distinct.has(id)) return true;
    if (frame.distinct.size >= MAX_DISTINCT_PER_FRAME) {
      this.emit(frame, "sound.load_error", { id, error: "distinct_cap" });
      return false;
    }
    frame.distinct.add(id);
    return true;
  }

  // ---- the context, the buses, the gates ------------------------------------------------

  private ensureContext(): void {
    if (this.ctx || this.disposed) return;
    const ctx = this.createContext();
    if (!ctx) return;
    this.ctx = ctx;
    const master = ctx.createGain();
    // An eval walk's `?wbm_mute=1` silences the speakers and nothing else (probeFlag.ts).
    master.gain.setValueAtTime(this.masterLevel(), ctx.currentTime);
    master.connect(ctx.destination);
    this.master = master;
    const mk = (): Bus => {
      const duck = ctx.createGain();
      duck.gain.setValueAtTime(this.voiceDucked ? VOICE_DUCK : 1, ctx.currentTime);
      duck.connect(master);
      const channel = ctx.createGain();
      channel.gain.setValueAtTime(0, ctx.currentTime);
      channel.connect(duck);
      return { channel, duck, loop: null };
    };
    this.buses = { sfx: mk(), ambient: mk(), music: mk() };
    this.applySettings(useAudioSettings.getState());
    if (ctx.state !== "running") this.armGesture();
  }

  /** An eval walk's mute, or the share page's: the master at 0, every bus and loop still running. */
  private masterLevel(): number {
    return evalMuteEnabled() || this.pageMuted ? 0 : 1;
  }

  /**
   * The share page's mute button (contract §15). Only the speakers go quiet: loops keep their
   * place and one-shots still play into a silent master, so turning it back on is where the
   * project is now, not a restart. Before the context exists it is remembered for when it does.
   */
  setPageMuted(on: boolean): void {
    this.pageMuted = on;
    const ctx = this.ctx, g = this.master?.gain;
    if (!ctx || !g) return;
    try {
      g.cancelScheduledValues(ctx.currentTime);
      g.setValueAtTime(g.value, ctx.currentTime);
      g.linearRampToValueAtTime(this.masterLevel(), ctx.currentTime + 0.1);
    } catch { /* ok */ }
  }

  isPageMuted(): boolean {
    return this.pageMuted;
  }

  private unlocked(): boolean {
    this.ensureContext();
    return !!this.ctx && this.ctx.state === "running";
  }

  /** `frame` decides the music bus on a surface whose bed ignores the Background music switch. */
  private channelOn(bus: BusName, frame?: Frame): boolean {
    const s = useAudioSettings.getState();
    if (bus === "music" && frame && BED_IGNORES_MUSIC_PREF.has(frame.meta.surface)) return WORLD_AUDIO_SFX_ENV_ENABLED;
    return bus === "sfx" ? sfxEnabled(s) : musicEnabled(s);
  }

  /** The music bus is open for the switch, or for the bed playing on it when its frame ignores the switch. */
  private musicChannelOn(s: AudioSettingsState): boolean {
    if (musicEnabled(s)) return true;
    const loop = this.buses?.music.loop;
    return !!loop && this.channelOn("music", this.frames.get(loop.frameId));
  }

  private rampChannel(bus: BusName, on: boolean): void {
    if (!this.ctx || !this.buses) return;
    const t = this.ctx.currentTime;
    const g = this.buses[bus].channel.gain;
    try {
      g.cancelScheduledValues(t);
      g.setValueAtTime(g.value, t);
      g.linearRampToValueAtTime(on ? BUS_BASE[bus] : 0, t + 0.1);
    } catch { /* ok */ }
  }

  private applySettings(s: AudioSettingsState): void {
    if (this.ctx && this.buses) {
      const music = this.musicChannelOn(s);
      this.rampChannel("sfx", sfxEnabled(s));
      this.rampChannel("ambient", musicEnabled(s));
      this.rampChannel("music", music);
      if (!musicEnabled(s)) {
        // Keep each loop as wanted so turning the channel back on brings it back.
        for (const [frameId, frame] of this.frames) {
          for (const bus of ["ambient", "music"] as const) {
            const loop = this.buses[bus].loop;
            if (bus === "music" && music) continue; // a shared project's bed plays on
            if (loop && loop.frameId === frameId && !frame.wanted[bus]) {
              frame.wanted[bus] = { id: loop.id, volume: loop.gain.gain.value || 1, entry: this.last(frameId, bus) };
            }
          }
        }
        this.stopLoop("ambient");
        if (!music) this.stopLoop("music");
      }
    }
    // The Sound tab turned everything off: the project's sound stopped for good, until a
    // sound plays again (which asks again).
    if (!sfxEnabled(s) && !this.musicChannelOn(s)) this.releasePlayback();
    // A loop asked for while the channel was off (no context yet, even) starts now.
    if (musicEnabled(s)) {
      for (const [frameId, frame] of this.frames) {
        void this.startWanted(frame, frameId, "ambient");
        void this.startWanted(frame, frameId, "music");
      }
    }
  }

  /**
   * A one-shot that arrived before the context runs. If a gesture has a resume in flight
   * (the tap that made this sound is the tap that unlocked us), it waits for that and plays;
   * otherwise it is dropped, because a late one-shot is wrong, not late (contract §5).
   */
  private deferShot(frameId: string, msg: WbmSoundMessage, entry: SoundLogEntry): void {
    if (this.resuming || this.pendingShots.length > 0) {
      entry.result = "queued";
      this.pendingShots.push({ frameId, msg, entry, t: this.now() });
      return;
    }
    entry.result = "dropped";
    entry.reason = "no_gesture";
    this.armGesture();
  }

  /**
   * Nothing plays before a user gesture (R4.3). `prime()` arms this at boot, so the first
   * pointer/key on the host (our own chrome, before any frame) resumes the context; the
   * frame's own first touch arrives as a `gesture` message. Either way the loops frames
   * asked for meanwhile start, and one-shots that waited on the resume play.
   */
  private armGesture(): void {
    if (this.gestureArmed || !this.doc) return;
    this.gestureArmed = true;
    // Every event of a tap asks, and the listener stays until the context runs: iOS WebKit
    // refuses a resume on pointerdown and honours it on touchend/click, so dropping the
    // listener on the first event threw away the one that counts (2026-10-27).
    this.onGesture = () => {
      this.hidden = this.doc!.visibilityState === "hidden";
      this.unlock();
    };
    for (const ev of GESTURE_EVENTS) this.doc.addEventListener(ev, this.onGesture, true);
  }

  private disarmGesture(): void {
    if (!this.gestureArmed || !this.doc || !this.onGesture) return;
    for (const ev of GESTURE_EVENTS) this.doc.removeEventListener(ev, this.onGesture, true);
    this.gestureArmed = false;
    this.onGesture = null;
  }

  /** Resume the context on a gesture (ours or the frame's) and release what waited. */
  private unlock(): void {
    this.ensureContext();
    const ctx = this.ctx;
    if (!ctx) return;
    const go = () => {
      this.resuming = null;
      if (ctx.state !== "running") { this.refused(); return; }
      this.disarmGesture();
      for (const [frameId, frame] of this.frames) {
        void this.startWanted(frame, frameId, "ambient");
        void this.startWanted(frame, frameId, "music");
      }
      this.flushPending();
    };
    if (ctx.state === "running") { go(); return; }
    let p: Promise<void>;
    try {
      p = Promise.resolve(ctx.resume());
    } catch {
      p = Promise.reject(new Error("resume threw"));
    }
    // A resume already in flight may be the one WebKit refused (a pointerdown's); this one is
    // asked inside the later event, and the first to land releases what waited.
    if (this.resuming) { p.then(() => { if (this.resuming) go(); }, () => {}); return; }
    this.resuming = p.then(go, () => { this.resuming = null; this.refused(); });
  }

  /**
   * The resume came back without a running context. The shots that waited keep waiting for
   * the next gesture (a tap's touchend lands ~100 ms after its pointerdown) and play if it
   * comes within PENDING_SHOT_MS of them; after that, or with no gesture, they are dropped.
   */
  private refused(): void {
    this.armGesture();
    if (this.pendingShots.length === 0) return;
    setTimeout(() => {
      if (!this.ctx || this.ctx.state !== "running") this.dropPending();
    }, PENDING_SHOT_MS);
  }

  private flushPending(): void {
    const shots = this.pendingShots.splice(0);
    const now = this.now();
    for (const s of shots) {
      const frame = this.frames.get(s.frameId);
      if (!frame || now - s.t > PENDING_SHOT_MS) { s.entry.result = "dropped"; s.entry.reason = "stale"; continue; }
      if (s.msg.op === "note") this.handleNote(frame, s.frameId, s.msg, s.entry);
      else this.handlePlay(frame, s.frameId, s.msg, s.entry);
    }
  }

  private dropPending(): void {
    for (const s of this.pendingShots.splice(0)) { s.entry.result = "dropped"; s.entry.reason = "no_gesture"; }
  }

  private listen(): void {
    if (this.listening) return;
    this.listening = true;
    this.unsubscribeSettings = useAudioSettings.subscribe((s) => this.applySettings(s));
    this.doc?.addEventListener("visibilitychange", this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    this.setHidden(this.doc?.visibilityState === "hidden");
  };

  // ---- the platform's audio session (train 13) ------------------------------------------

  /** A sound is actually playing: ask the platform once; it stays ours until released. */
  private holdPlayback(): void {
    if (this.playbackHeld) return;
    this.playbackHeld = true;
    const session = this.playbackSession();
    if (!session) return;
    try {
      void Promise.resolve(session.requestSoundPlayback()).catch(() => { /* the adapter never throws; belt and braces */ });
    } catch { /* same */ }
  }

  /** The project's sound stopped for good (player unmounted, Sound tab off, host disposed). */
  private releasePlayback(): void {
    if (!this.playbackHeld) return;
    this.playbackHeld = false;
    const session = this.playbackSession();
    if (!session) return;
    try {
      void Promise.resolve(session.releaseSoundPlayback()).catch(() => { /* ok */ });
    } catch { /* ok */ }
  }

  private playbackSession(): PlaybackSession | null {
    if (this.playback) return this.playback;
    try {
      this.playback = this.playbackFactory?.() ?? null;
    } catch {
      this.playback = null;
    }
    return this.playback;
  }

  // ---- the caps -------------------------------------------------------------------------

  private capped(id: string): boolean {
    if (this.voices.size >= MAX_VOICES) return true;
    const now = this.now();
    const starts = (this.startsById.get(id) ?? []).filter((t) => now - t < START_WINDOW_MS);
    this.startsById.set(id, starts);
    return starts.length >= MAX_STARTS_PER_ID;
  }

  private noteStart(id: string): void {
    const starts = this.startsById.get(id) ?? [];
    starts.push(this.now());
    this.startsById.set(id, starts);
  }

  private validId(id: unknown): string | null {
    return typeof id === "string" && SOUND_ID_RE.test(id) ? id : null;
  }

  // ---- observability --------------------------------------------------------------------

  private missing(frame: Frame, entry: SoundLogEntry, reason: string): void {
    entry.result = "missing";
    this.emit(frame, "sound.missing", { id: entry.requested, reason });
  }

  private emitPlayed(frame: Frame, row: SoundRow, op: string): void {
    this.playedCount++;
    if (this.playedCount % PLAYED_SAMPLE !== 0) return;
    this.emit(frame, "sound.played", { id: row.id, kind: row.kind, op });
  }

  private emitCapped(frame: Frame, id: string): void {
    this.cappedCount++;
    if (this.cappedCount % CAPPED_SAMPLE !== 1) return;
    this.emit(frame, "sound.voice_capped", { id, voices: this.voices.size });
  }

  private emit(frame: Frame, event_type: string, data: Record<string, unknown>): void {
    if (!frame.meta.userId) {
      if (!frame.meta.report) return;
      try {
        frame.meta.report(event_type, frame.meta.projectId, { ...data, vertical: frame.meta.vertical, surface: frame.meta.surface });
      } catch { /* telemetry never throws */ }
      return;
    }
    try {
      this.emitFn({
        event_type,
        user_id: frame.meta.userId,
        project_id: frame.meta.projectId ?? undefined,
        event_data: { ...data, vertical: frame.meta.vertical, surface: frame.meta.surface },
      });
    } catch { /* telemetry never throws */ }
  }

  /** For events with no frame in hand (a decode failure): any frame that can send. */
  private emitAny(event_type: string, data: Record<string, unknown>): void {
    for (const frame of this.frames.values()) {
      if (frame.meta.userId || frame.meta.report) { this.emit(frame, event_type, data); return; }
    }
  }

  private push(entry: SoundLogEntry): void {
    this.log.push(entry);
    if (this.log.length > LOG_MAX) this.log.splice(0, this.log.length - LOG_MAX);
  }

  // ---- the probe ------------------------------------------------------------------------

  probe(): SoundProbe {
    const gainOf = (bus: BusName): { gain: number; duck: number } => {
      const b = this.buses?.[bus];
      return b
        ? { gain: Number(b.channel.gain.value.toFixed(3)), duck: Number(b.duck.gain.value.toFixed(3)) }
        : { gain: 0, duck: 1 };
    };
    return {
      enabled: this.enabled,
      unlocked: !!this.ctx && this.ctx.state === "running",
      ctxState: this.ctx ? (this.ctx.state as SoundProbe["ctxState"]) : "none",
      indexed: this.index?.rows.size ?? 0,
      index: this.index?.source ?? "live",
      buses: {
        sfx: { ...gainOf("sfx"), id: null },
        ambient: { ...gainOf("ambient"), id: this.buses?.ambient.loop?.id ?? null },
        music: { ...gainOf("music"), id: this.buses?.music.loop?.id ?? null },
      },
      worldAmbientGain: getActiveWorldAudio()?.duckFactor ?? 1,
      voices: this.voices.size,
      pageMuted: this.pageMuted,
      log: this.log.slice(),
    };
  }

  dispose(): void {
    this.disposed = true;
    for (const [frameId, frame] of this.frames) this.stopFrame(frame, frameId);
    this.frames.clear();
    this.releasePlayback();
    this.unsubscribeSettings?.();
    this.unsubscribeSettings = null;
    this.doc?.removeEventListener("visibilitychange", this.onVisibility);
    this.disarmGesture();
    try { if (this.ctx) void this.ctx.close(); } catch { /* ok */ }
    this.ctx = null;
    this.buses = null;
    this.buffers.clear();
  }
}

// ---- the page's one host ------------------------------------------------------------------

let activeHost: SoundHost | null = null;

/** The one host this page has, made on first use. */
export function getSoundHost(): SoundHost {
  if (!activeHost) activeHost = new SoundHost();
  return activeHost;
}

/** Tests only: drop the page host so the next `getSoundHost()` builds a fresh one. */
export function resetSoundHostForTests(host: SoundHost | null = null): void {
  activeHost?.dispose();
  activeHost = host;
}
